import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from "@/components/ui/SidePanel";
import { Button } from "@/components/ui/Button";
import { useUpdateUserProfileMutation } from "@/api/clubsApi";
import type { UserProfile } from "@/api/clubsApi";
import { cn } from "@/components/ui/cn";

interface EditAboutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: UserProfile;
}

const inputClassName = cn(
  "w-full rounded-2xl border border-border-tertiary p-3",
  "text-base text-text-primary bg-input-background",
  "placeholder:text-text-tertiary",
  "focus:outline-none focus:ring-2 focus:ring-text-primary focus:ring-offset-2 focus:ring-offset-background",
  "focus:border-text-primary"
);

export function EditAboutModal({
  open,
  onOpenChange,
  profile,
}: EditAboutModalProps) {
  const [updateProfile, { isLoading: isSaving }] = useUpdateUserProfileMutation();

  // Form state
  const [locationName, setLocationName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [educationName, setEducationName] = useState("");

  // Initialize form state when modal opens
  useEffect(() => {
    if (open && profile) {
      // Extract location display from location_object if available
      const locationObject = profile.location_object as {
        properties?: {
          context?: {
            place?: { name?: string };
            region?: { name?: string };
          };
        };
      } | null;
      const locationCity = locationObject?.properties?.context?.place?.name;
      const locationState = locationObject?.properties?.context?.region?.name;
      const location =
        locationCity && locationState
          ? `${locationCity}, ${locationState}`
          : profile.location_name || "";

      setLocationName(location);
      setJobTitle(profile.job_title || "");
      setBirthdate(profile.birthdate || "");
      setEducationName(profile.education_name || "");
    }
  }, [open, profile]);

  const handleSave = async () => {
    try {
      await updateProfile({
        id: profile.id,
        location_name: locationName.trim() || null,
        job_title: jobTitle.trim() || null,
        birthdate: birthdate || null,
        education_name: educationName.trim() || null,
        updated_at: DateTime.now().toISO(),
      }).unwrap();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  };

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Edit about" onClose={() => onOpenChange(false)} />

        <SidePanelBody className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-body-3 text-text-secondary">Location</label>
            <input
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="Where do you live?"
              className={inputClassName}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-body-3 text-text-secondary">Job title</label>
            <input
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="What do you do?"
              className={inputClassName}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-body-3 text-text-secondary">Birthday</label>
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              className={inputClassName}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-body-3 text-text-secondary">Education</label>
            <input
              value={educationName}
              onChange={(e) => setEducationName(e.target.value)}
              placeholder="Where did you study?"
              className={inputClassName}
            />
          </div>
        </SidePanelBody>

        <SidePanelFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-text-primary text-text-reversed hover:bg-text-primary/90"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </SidePanelFooter>
      </SidePanelContent>
    </SidePanel>
  );
}
